import { useContext, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { extractTextFromImage, extractTextFromPDF, showImage } from "../../../utils";
import { toast, ToastContainer } from "react-toastify";
import { request } from "../../../utils/api";
import { AuthContext } from "../../../auth/AuthContext";
import Loading from "../utilComponent/Loading";
import NotFoundTitle from "../utilComponent/NotFoundTitle";
import Pagination from "../utilComponent/Pagination";

const UploadLabel = () => {
  const { user } = useContext(AuthContext);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [tracking, setTracking] = useState({});
  const [quantity, setQuantity] = useState({});
  const [uploadLoading, setUploadLoading] = useState({});
  const [fileName, setFileName] = useState("");
  const [uploadMode, setUploadMode] = useState("one-to-one");

  // For many-to-one mode
  const [selectedProducts, setSelectedProducts] = useState([]);
  const [multiTracking, setMultiTracking] = useState("");
  const [multiFileName, setMultiFileName] = useState("");
  const [multiQuantity, setMultiQuantity] = useState({});
  const [multiUploadLoading, setMultiUploadLoading] = useState(false);
  const [labelFile, setLabelFile] = useState(null);
  const [multiLabelFile, setMultiLabelFile] = useState(null);

  const [pagination, setPagination] = useState({
    current_page: 1,
    per_page: 15,
    total: 0,
    last_page: 1,
  });

  const isAdmin = user?.role_key === "admin";
  const isSuperAdmin = user?.role_key === "super_admin";
  
  const extractTrackingNumber = (text) => {
    const clean = text.replace(/[^0-9A-Z]/gi, "").toUpperCase();
    return (
      clean.match(/1Z[0-9A-Z]{16}/)?.[0] ||
      clean.match(/3936[0-9A-Z]{8,20}/)?.[0] ||
      clean.match(/339VA[0-9A-Z]{8,20}/)?.[0] ||
      clean.match(/B5[0-9A-Z]{8,20}/)?.[0] ||
      clean.match(/9400[0-9A-Z]{8,20}/)?.[0] ||
      ""
    );
  };
  const handlePDFUpload = async (e, productId) => {
    const file = e.target.files[0];
    if (!file) return;

    uploadMode === "one-to-one"
      ? setLabelFile(file)
      : setMultiLabelFile(file);

    if (uploadMode === "one-to-one") {
      setFileName(file.name);
    } else {
      setMultiFileName(file.name);
    }

    try {
      setLoading(true);
      const text = await extractTextFromPDF(file);
      if (uploadMode === "one-to-one") {
        setTracking((prev) => ({
          ...prev,
          [productId]:  extractTrackingNumber(text) || `Label Uploaded`,
        }));
      } else {
        const tracking = extractTrackingNumber(text) || `Label Uploaded`;
        setMultiTracking(tracking);
      }
    } catch {
      toast.error("Failed to read PDF");
    } finally {
      setLoading(false);
    }
  };

  const handleImageUpload = async (e, productId) => {
    const file = e.target.files[0];
    if (!file) return;
    if (uploadMode === "one-to-one") {
      setFileName(file?.name || "Youbas-label-upload");
    } else {
      setMultiFileName(file?.name || "Youbas-label-upload");
    }
    try {
      setLoading(true);
      const text = await extractTextFromImage(file);
      if (uploadMode === "one-to-one") {
        setTracking((prev) => ({
          ...prev,
          [productId]: extractTrackingNumber(text),
        }));
      } else {
        setMultiTracking(extractTrackingNumber(text));
      }
    } catch {
      toast.error("Failed to read image");
    } finally {
      setLoading(false);
    }
  };

  const getProductList = async (page = pagination.current_page) => {
    if (!user) return;
    let apiUrl;
    if(isAdmin || isSuperAdmin) {
      apiUrl = `products?page=${page}&per_page=${pagination.per_page}`;
    } else {
      apiUrl = `assign-products/${user.id}?page=${page}&per_page=${pagination.per_page}`;
    }
    
    try {
      setLoading(true);
      const res = await request({
        url: apiUrl,
        method: "GET",
      });
      setProducts(res?.data?.assigned_products?.items || res?.data?.items || []);
      setPagination(
        res?.data?.assigned_products?.pagination ||
          res?.data?.pagination ||
          pagination
      );
    } finally {
      setLoading(false);
    }
  };

  const handlePageChange = (page) => {
    setPagination((prev) => ({ ...prev, current_page: page }));
    setSelectedProducts([]);
    setMultiQuantity({});
  };

  const buildFormData = (item, file) => {
    const fd = new FormData();

    fd.append(`labels[0][product_id]`, item.id);
    fd.append(`labels[0][user_id]`, user.id);
    fd.append(`labels[0][label_name]`, fileName);
    fd.append(`labels[0][product_qty]`, quantity[item.id]);
    fd.append(`labels[0][label_extract_no]`, tracking[item.id] || "");
    fd.append(`labels[0][track_status]`, "Label Created");
    fd.append(`prep_status`, "Label Created");
    fd.append(`labels[0][notes]`, "");
    fd.append(`label_files[0]`, file);

    return fd;
  };


  const buildMultiFormData = (file) => {
    const fd = new FormData();

    selectedProducts.forEach((productId, index) => {
      fd.append(`labels[${index}][product_id]`, productId);
      fd.append(`labels[${index}][user_id]`, user.id);
      fd.append(`labels[${index}][label_name]`, multiFileName);
      fd.append(`labels[${index}][product_qty]`, multiQuantity[productId]);
      fd.append(`labels[${index}][label_extract_no]`, multiTracking || "");
      fd.append(`labels[${index}][track_status]`, "Label Created");
      fd.append(`prep_status`, "Label Created");
      fd.append(`labels[${index}][notes]`, "");
    });
    fd.append(`label_files[0]`, file);

    return fd;
  };


  const uploadLabel = async (item) => {
    if (!quantity[item.id]) {
      toast.error("Please enter product quantity");
      return;
    }
    setUploadLoading((prev) => ({ ...prev, [item.id]: true }));
    try {
      await request({
        url: "labels",
        method: "POST",
        data: buildFormData(item, labelFile),
      });
      toast.success("Label uploaded successfully");
      setTracking((prev) => ({ ...prev, [item.id]: "" }));
      setQuantity((prev) => ({ ...prev, [item.id]: "" }));
    } catch {
      toast.error("Upload failed");
    } finally {
      setUploadLoading((prev) => ({ ...prev, [item.id]: false }));
    }
  };

  const uploadMultiLabel = async () => {
    if (selectedProducts.length === 0) {
      toast.error("Please select at least one product");
      return;
    }

    const hasInvalidQuantity = selectedProducts.some(
      (productId) => !multiQuantity[productId] || multiQuantity[productId] <= 0
    );
    if (hasInvalidQuantity) {
      toast.error("Please enter product quantity for all selected products");
      return;
    }

    setMultiUploadLoading(true);
    try {
      await request({
        url: "labels",
        method: "POST",
        data: buildMultiFormData(multiLabelFile),
      });
      toast.success("Label uploaded successfully for all selected products");
      setMultiTracking("");
      setMultiFileName("");
      setMultiQuantity({});
      setSelectedProducts([]);
    } catch {
      toast.error("Upload failed");
    } finally {
      setMultiUploadLoading(false);
    }
  };

  const toggleProductSelection = (productId) => {
    setSelectedProducts((prev) => {
      if (prev.includes(productId)) {
        return prev.filter((id) => id !== productId);
      } else {
        return [...prev, productId];
      }
    });
  };

  const isAllSelected =
    products.length > 0 && selectedProducts.length === products.length;

  const handleSelectAll = () => {
    if (isAllSelected) {
      setSelectedProducts([]);
      setMultiQuantity({});
    } else {
      const allIds = products.map((p) => p.id);
      setSelectedProducts(allIds);

      setMultiQuantity((prev) => {
        const updated = { ...prev };
        allIds.forEach((id) => {
          if (!updated[id]) updated[id] = "";
        });
        return updated;
      });
    }
  };

  useEffect(() => {
    getProductList(pagination.current_page);
  }, [user, pagination.current_page]);

  return (
    <>
      <ToastContainer />
      <div className="page-toolbar px-xl-4 px-sm-2 px-0 py-3">
        <div className="container-fluid">
          <div className="row g-3 mb-3 align-items-center">
            <div className="col">
              <ol className="breadcrumb bg-transparent mb-0">
                <li className="breadcrumb-item">
                  <Link className="text-secondary" to="/">
                    Home
                  </Link>
                </li>
                <li className="breadcrumb-item">
                  <span className="text-secondary">Label</span>
                </li>
                <li className="breadcrumb-item active">Upload Label</li>
              </ol>
            </div>
          </div>
        </div>
      </div>

      <div className="card mx-4 p-4">
        {loading && <Loading />}

        {!loading && products.length === 0 ? (<NotFoundTitle title="No Label found here" />) : <>
          <div className="mb-4">
            <div className="d-flex align-items-center justify-content-between">
              <h5 className="mb-0">Upload Mode:</h5>
              <div className="form-check form-switch">
                <input
                  className="form-check-input"
                  type="checkbox"
                  id="uploadModeSwitch"
                  checked={uploadMode === "many-to-one"}
                  onChange={(e) => {
                    const newMode = e.target.checked
                      ? "many-to-one"
                      : "one-to-one";
                    setUploadMode(newMode);
                    if (newMode === "one-to-one") {
                      setMultiTracking("");
                      setMultiFileName("");
                      setMultiQuantity({});
                      setSelectedProducts([]);
                    } else {
                      setTracking({});
                      setQuantity({});
                      setFileName("");
                    }
                  }}
                  style={{ width: "3rem", height: "1.5rem", cursor: "pointer" }}
                />
                <label
                  className="form-check-label ms-2"
                  htmlFor="uploadModeSwitch"
                >
                  {uploadMode === "one-to-one" ? (
                    <span>One Product - One Label</span>
                  ) : (
                    <span>Multiple Products - One Label</span>
                  )}
                </label>
              </div>
            </div>
          </div>
          {uploadMode === "one-to-one" && (
            <div className="row g-4">
              {products?.map((item) => (
                <div className="col-md-3" key={item.id}>
                  <div className="card h-100 position-relative">
                    {uploadLoading[item.id] && (
                      <div
                        className="position-absolute top-0 start-0 w-100 h-100 bg-white bg-opacity-75 d-flex justify-content-center align-items-center"
                        style={{ zIndex: 10 }}
                      >
                        <div className="spinner-border text-primary"></div>
                      </div>
                    )}

                    <img
                      src={showImage(item.images[0]?.url)}
                      alt={item.name}
                      className="card-img-top"
                      style={{ width:"100%",height: 220, objectFit: "cover" }}
                    />

                    <div className="card-body">
                      <h5>{item.name}</h5>
                      <p>
                        <strong>SKU:</strong> {item.sku}
                      </p>

                      {tracking[item.id] && (
                        <div className="alert alert-info mb-2">
                          <small>
                            <strong>Tracking:</strong> {tracking[item.id]}
                          </small>
                        </div>
                      )}

                      <label className="btn btn-danger w-100 mb-2">
                        Upload PDF
                        <input
                          type="file"
                          hidden
                          accept="application/pdf"
                          onChange={(e) => handlePDFUpload(e, item.id)}
                        />
                      </label>

                      <label className="btn btn-primary w-100 mb-2">
                        Upload Image / Camera
                        <input
                          type="file"
                          hidden
                          accept="image/*"
                          capture="environment"
                          onChange={(e) => handleImageUpload(e, item.id)}
                        />
                      </label>

                      <input
                        type="number"
                        className="form-control mt-2"
                        placeholder="Product Quantity"
                        value={quantity[item.id] || ""}
                        onChange={(e) =>
                          setQuantity((prev) => ({
                            ...prev,
                            [item.id]: e.target.value,
                          }))
                        }
                      />

                      <button
                        className="btn btn-success w-100 mt-3 d-flex align-items-center justify-content-center"
                        disabled={uploadLoading[item.id]}
                        onClick={() => uploadLabel(item)}
                      >
                        {uploadLoading[item.id] ? (
                          <>
                            <span className="spinner-border spinner-border-sm me-2"></span>
                            Saving...
                          </>
                        ) : (
                          "Save Label"
                        )}
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {uploadMode === "many-to-one" && (
            <>
              <div className="card mb-4">
                <div className="card-body">
                  <h5 className="mb-3">Upload Label (for multiple products)</h5>

                  {multiTracking && (
                    <div className="alert alert-info mb-3">
                      <strong>Tracking Number:</strong> {multiTracking}
                    </div>
                  )}

                  <div className="row g-2 mb-3">
                    <div className="col-md-6">
                      <label className="btn btn-danger w-100">
                        Upload PDF
                        <input
                          type="file"
                          hidden
                          accept="application/pdf"
                          onChange={(e) => handlePDFUpload(e, null)}
                        />
                      </label>
                    </div>
                    <div className="col-md-6">
                      <label className="btn btn-primary w-100">
                        Upload Image / Camera
                        <input
                          type="file"
                          hidden
                          accept="image/*"
                          capture="environment"
                          onChange={(e) => handleImageUpload(e, null)}
                        />
                      </label>
                    </div>
                  </div>
                </div>
              </div>
              <div className="d-flex justify-content-end mb-3">
                <button
                  type="button"
                  className={`btn ${isAllSelected ? "btn-outline-danger" : "btn-outline-primary"
                    }`}
                  onClick={handleSelectAll}
                >
                  {isAllSelected ? "Unselect All" : "Select All"}
                </button>
              </div>

              <div className="row g-4">
                {products.map((item) => (
                  <div className="col-md-3" key={item.id}>
                    <div className="card h-100">
                      <div className="card-body">
                        <div className="form-check mb-3">
                          <input
                            className="form-check-input"
                            type="checkbox"
                            id={`product-${item.id}`}
                            checked={selectedProducts.includes(item.id)}
                            onChange={() => toggleProductSelection(item.id)}
                          />

                          <label
                            className="form-check-label"
                            htmlFor={`product-${item.id}`}
                          >
                            <strong>{item.name}</strong>
                          </label>
                        </div>

                        <img
                          src={showImage(item.images[0]?.url)}
                          alt={item.name}
                          className="card-img-top"
                          style={{ width:"100%",height: 220, objectFit: "cover" }}
                        />
                        <p className="mb-2">
                          <strong>SKU:</strong> {item.sku}
                        </p>

                        {selectedProducts.includes(item.id) && (
                          <input
                            type="number"
                            className="form-control"
                            placeholder="Product Quantity"
                            value={multiQuantity[item.id] || ""}
                            onChange={(e) =>
                              setMultiQuantity((prev) => ({
                                ...prev,
                                [item.id]: e.target.value,
                              }))
                            }
                          />
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
              <div className="mt-4">
                <button
                  className="btn btn-success w-100 d-flex align-items-center justify-content-center"
                  disabled={multiUploadLoading || selectedProducts.length === 0}
                  onClick={uploadMultiLabel}
                  style={{ maxWidth: "300px", margin: "0 auto" }}
                >
                  {multiUploadLoading ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-2"></span>
                      Saving...
                    </>
                  ) : (
                    `Save Label for ${selectedProducts.length} Product(s)`
                  )}
                </button>
              </div>
            </>
          )}

          {pagination.last_page > 1 && (
            <Pagination
              currentPage={pagination.current_page}
              lastPage={pagination.last_page}
              onPageChange={handlePageChange}
            />
          )}
        </>}

      </div>
    </>
  );
};

export default UploadLabel;
